import { CommonModule } from '@angular/common';
import { Component, DestroyRef, inject, OnInit, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatTableModule } from '@angular/material/table';
import { TranslateModule } from '@ngx-translate/core';
import { firstValueFrom } from 'rxjs';

import { NutrientPagePressEvent } from '@module-definition/nutrient/common';
import { PdfMarkupAssociation, PdfMarkupKeyValue, PdfMarkupService } from '@module-instance/pdf-markups/common';
import { CipoSelectComponent } from '@shared/components/fields/cipo-select/cipo-select.component';
import { CipoListConfig, CipoListControl } from '@shared/components/fields/common';
import { CipoDialogHeaderComponent } from '@shared/dialogs';
import { CipoAutofocusDirective } from '@shared/directives/cipo-autofocus.directive';
import { CipoDialogModule } from '@shared/modules';

export type NutrientDocumentLinkDialogData = {
  documentId: string;
  entityInstanceId: number;
  annotationClick?: NutrientPagePressEvent;
  createAnnotation?: (moduleTypeInstanceId: number, annotationClick: NutrientPagePressEvent) => Promise<void>;
  deleteAnnotationOnly?: (moduleTypeInstanceId: number) => Promise<void>;
  deleteAnnotation?: (moduleTypeInstanceId: number) => Promise<void>;
  canDelete?: boolean;
};

@Component({
  selector: 'cipo-nutrient-document-link-dialog',
  imports: [
    CipoDialogModule,
    CipoDialogHeaderComponent,
    CommonModule,
    TranslateModule,
    MatTableModule,
    CipoSelectComponent,
    CipoAutofocusDirective,
  ],
  providers: [PdfMarkupService],
  templateUrl: './nutrient-document-link-dialog.component.html',
})
export class NutrientDocumentLinkDialogComponent implements OnInit {
  private dialogRef = inject(MatDialogRef<NutrientDocumentLinkDialogComponent>);
  private pdfMarkupService = inject(PdfMarkupService);
  private destroyRef = inject(DestroyRef);
  data = inject<NutrientDocumentLinkDialogData>(MAT_DIALOG_DATA);

  associations = signal<(PdfMarkupAssociation & { confirmDeletion?: boolean })[]>([]);
  isLoading = signal(true);

  isSaving = signal(false);
  isLoadingDocs = signal(false);
  message = signal('');
  errorMessage = signal('');

  moduleTypeControl = new CipoListControl(null);
  documentControl = new CipoListControl(null);

  moduleTypeFieldData = signal<CipoListConfig>({
    label: 'pdfMarkups.docLink.docType',
    required: true,
    options: [],
  });

  documentFieldData = signal<CipoListConfig>({
    label: 'pdfMarkups.docLink.document',
    required: true,
    options: [],
  });

  displayedColumns = ['name', 'createdBy', 'createdOn', 'actions'];

  get isAddAssociation() {
    return !!this.data.annotationClick;
  }

  ngOnInit() {
    this.initializeDialogData();
  }

  private initializeDialogData() {
    if (this.isAddAssociation) {
      this.documentControl.disable();
      this.message.set('general.loadingMessage');
      this.pdfMarkupService.getLinkableModules(this.data.entityInstanceId).subscribe({
        next: modules => {
          this.moduleTypeFieldData.update(fd => ({ ...fd, options: modules }));
          this.message.set('pdfMarkups.docLink.selectPrompt');
          this.isLoading.set(false);
        },
        error: () => this.isLoading.set(false),
      });

      this.moduleTypeControl.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(v => {
        if (v != null && !this.isSaving()) this.onModuleTypeChange(v);
      });
    } else {
      this.pdfMarkupService.getAssociatedDocuments(this.data.documentId, this.data.entityInstanceId).subscribe({
        next: docs => {
          this.associations.set(docs);
          this.isLoading.set(false);
        },
        error: () => this.isLoading.set(false),
      });
    }
  }

  onModuleTypeChange(moduleTypeId: number) {
    this.documentControl.setValue(null);
    this.documentControl.disable();
    this.documentFieldData.update(fd => ({ ...fd, options: [] }));
    this.message.set('pdfMarkups.docLink.loadingDocs');
    this.isLoadingDocs.set(true);
    this.pdfMarkupService
      .getLinkableDocuments(this.data.entityInstanceId, moduleTypeId, this.data.documentId)
      .subscribe({
        next: (docs: PdfMarkupKeyValue[]) => {
          this.documentFieldData.update(fd => ({ ...fd, options: docs }));
          this.message.set('pdfMarkups.docLink.selectPrompt');
          this.documentControl.enable();
          this.isLoadingDocs.set(false);
        },
        error: () => {
          this.documentControl.enable();
          this.isLoadingDocs.set(false);
        },
      });
  }

  async save(): Promise<void> {
    const moduleTypeInstanceId = this.documentControl.value as number;
    if (!moduleTypeInstanceId) return;

    this.errorMessage.set('');
    this.isSaving.set(true);
    this.moduleTypeControl.disable();
    this.documentControl.disable();
    this.message.set('pdfMarkups.docLink.saving');
    let annotationCreated = false;
    try {
      if (!this.data.createAnnotation || !this.data.deleteAnnotationOnly || !this.data.annotationClick) {
        throw new Error('Missing annotation callback data');
      }

      // Create in Nutrient first: an Update-only user cannot call the Delete-protected API to roll back a DB link.
      await this.data.createAnnotation(moduleTypeInstanceId, this.data.annotationClick);
      annotationCreated = true;
      const association = await firstValueFrom(
        this.pdfMarkupService.saveDocumentLink(this.data.entityInstanceId, this.data.documentId, moduleTypeInstanceId),
      );
      this.dialogRef.close(association);
    } catch {
      if (annotationCreated) {
        try {
          await this.data.deleteAnnotationOnly?.(moduleTypeInstanceId);
        } catch {
          // The save error remains the actionable failure; the viewer will surface its own SDK error.
        }
      }
      this.errorMessage.set('pdfMarkups.docLink.saveError');
      this.resetSaveState();
    }
  }

  private resetSaveState() {
    this.isSaving.set(false);
    this.moduleTypeControl.enable();
    this.documentControl.enable();
    this.message.set('pdfMarkups.docLink.selectPrompt');
  }

  toggleDeleteLink(association: PdfMarkupAssociation & { confirmDeletion?: boolean }) {
    association.confirmDeletion = !association.confirmDeletion;
  }

  async deleteLink(
    association: PdfMarkupAssociation & { confirmDeletion?: boolean; deleting?: boolean },
  ): Promise<void> {
    if (!this.data.canDelete || association.deleting) return;

    this.errorMessage.set('');
    association.deleting = true;
    this.associations.update(list => [...list]);
    try {
      if (this.data.deleteAnnotation) {
        await this.data.deleteAnnotation(association.moduleTypeInstanceId);
      } else {
        await firstValueFrom(
          this.pdfMarkupService.deleteDocumentLink(
            this.data.documentId,
            association.moduleTypeInstanceId,
            this.data.entityInstanceId,
          ),
        );
      }
      this.associations.update(list => list.filter(a => a.moduleTypeInstanceId !== association.moduleTypeInstanceId));
    } catch {
      association.deleting = false;
      this.associations.update(list => [...list]);
      this.errorMessage.set('pdfMarkups.docLink.deleteError');
    }
  }

  close() {
    this.dialogRef.close();
  }
}
